<template>
  <section class="admin-content">
    <Navbar />
    <main class="admin-main">

      <!-- Page Header -->
      <div style="background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); padding: 32px 0;">
        <div class="container">
          <div class="d-flex align-items-center text-white">
            <div class="mr-3"
              style="width: 48px; height: 48px; background: rgba(255,255,255,0.2); border-radius: 12px; display: flex; align-items: center; justify-content: center;">
              <i class="fe fe-shuffle" style="font-size: 22px;"></i>
            </div>
            <div>
              <h4 class="mb-0 font-weight-bold">Referrals</h4>
              <small style="opacity: 0.8;">Manage and verify authorization code referrals</small>
            </div>
          </div>
        </div>
      </div>

      <section style="margin-top: 20px;">
        <div class="container pb-5">

          <!-- Provider tabs -->
          <div v-if="user && (user.type == 'provider' || user.type == 'provider_employee')" class="mb-4 d-flex"
            style="gap: 8px;">
            <button type="button" :class="buttoncolor.formal" @click="showPrimary">
              <i class="fe fe-arrow-up-right mr-1"></i> Primary (Referring)
            </button>
            <button type="button" :class="buttoncolor.informal" @click="showSecondary">
              <i class="fe fe-arrow-down-left mr-1"></i> Secondary (Recipient)
            </button>
          </div>

          <!-- ── FACE SCAN CARD ─────────────────────────────────── -->
          <div class="card shadow-sm mb-4">
            <div class="card-header border-0 pt-4 pb-3 px-4" style="background: #f8f9fa;">
              <div class="d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <div class="mr-3"
                    style="width: 34px; height: 34px; background: linear-gradient(135deg, #1e3a5f, #2563eb); border-radius: 8px; display:flex; align-items:center; justify-content:center;">
                    <i class="fe fe-camera text-white" style="font-size: 16px;"></i>
                  </div>
                  <div>
                    <h6 class="mb-0 font-weight-bold text-dark">Face Scan</h6>
                    <small class="text-muted">Scan a patient's face to find their referral</small>
                  </div>
                </div>
                <div>
                  <button v-if="!faceScan.active" type="button" class="btn btn-primary" @click="startFaceScan"
                    :disabled="faceScan.loading">
                    <span v-if="faceScan.loading"><span class="spinner-border spinner-border-sm mr-1"></span> Loading
                      models…</span>
                    <span v-else><i class="fe fe-camera mr-1"></i> Scan Patient Face</span>
                  </button>
                  <button v-else type="button" class="btn btn-outline-secondary btn-sm" @click="resetFaceScan">
                    <i class="fe fe-x mr-1"></i> Close
                  </button>
                </div>
              </div>
            </div>

            <div class="card-body px-4 pb-4" v-if="faceScan.active">
              <div class="row">
                <!-- Captured image -->
                <div class="col-md-3 text-center mb-3 mb-md-0">
                  <p class="small text-muted mb-2 font-weight-bold text-uppercase"
                    style="letter-spacing: .04em; font-size: 0.7rem;">Scanned Face</p>
                  <div v-if="!faceScan.capturedImage"
                    class="rounded d-flex flex-column align-items-center justify-content-center mx-auto"
                    style="width: 150px; height: 150px; border: 3px dashed #b2bec3; background: #f8f9fa; cursor: pointer;"
                    @click="captureForScan">
                    <i class="fe fe-camera text-muted" style="font-size: 2rem;"></i>
                    <small class="text-muted mt-1">Tap to scan</small>
                  </div>
                  <div v-else>
                    <img :src="faceScan.capturedImage" class="rounded mx-auto d-block"
                      style="width: 150px; height: 150px; object-fit: cover; border: 3px solid #2563eb;" />
                    <button type="button" class="btn btn-outline-primary btn-sm mt-2 btn-block" @click="captureForScan"
                      :disabled="faceScan.scanning">
                      <i class="fe fe-refresh-cw mr-1"></i> Rescan
                    </button>
                  </div>
                </div>

                <!-- Results panel -->
                <div class="col-md-9">
                  <!-- Scanning progress -->
                  <div v-if="faceScan.scanning" class="d-flex align-items-center py-3 px-3 rounded"
                    style="background: #f0f4ff; border: 1px solid #d0d9ee;">
                    <div class="spinner-border text-primary mr-3" style="width: 24px; height: 24px;" role="status">
                    </div>
                    <div>
                      <p class="mb-0 font-weight-bold" style="font-size: 0.9rem;">Scanning referrals…</p>
                      <small class="text-muted">Checked {{ faceScan.progress }} of {{ faceScan.total }}
                        enrollees</small>
                      <div class="progress mt-1" style="height: 4px; width: 200px;">
                        <div class="progress-bar bg-primary"
                          :style="`width: ${faceScan.total ? (faceScan.progress/faceScan.total*100) : 0}%`"></div>
                      </div>
                    </div>
                  </div>

                  <!-- Idle -->
                  <div v-else-if="!faceScan.capturedImage" class="text-center py-4 text-muted">
                    <i class="fe fe-user-check" style="font-size: 2.5rem; opacity: 0.3;"></i>
                    <p class="mt-2 mb-0">Capture a face to find matching referrals from the list below</p>
                  </div>

                  <!-- Matches found -->
                  <div v-else-if="faceScan.matches.length">
                    <p class="small font-weight-bold text-success mb-2">
                      <i class="fe fe-check-circle mr-1"></i> {{ faceScan.matches.length }} match(es) found
                    </p>
                    <div v-for="match in faceScan.matches" :key="match.ref.id"
                      class="d-flex align-items-center p-3 rounded mb-2"
                      style="background: #f0fff4; border: 1px solid #a8e6b8;">
                      <img :src="match.ref.enrollee.image_url" class="rounded-circle mr-3"
                        style="width: 52px; height: 52px; object-fit: cover; border: 2px solid #28a745; flex-shrink: 0;"
                        onerror="this.onerror=null; this.src='/assets/img/claimify_logo.png'" />
                      <div class="flex-grow-1">
                        <p class="mb-0 font-weight-bold" style="font-size: 0.9rem;">{{ match.ref.enrollee.full_name }}
                        </p>
                        <small class="text-muted">
                          {{ match.ref.enrollee.id_card_number }}
                          <span class="mx-1">&middot;</span>
                          Code: <strong class="text-dark">{{ match.ref.code_created }}</strong>
                        </small><br>
                        <small class="text-muted">{{ match.ref.provider && match.ref.provider.agency_name }}</small>
                      </div>
                      <div class="text-right ml-2" style="flex-shrink: 0;">
                        <span class="badge badge-success d-block mb-1">{{ match.confidence }}% match</span>
                        <span :class="`badge badge-soft-${match.ref.color || 'secondary'} d-block mb-1`">{{
                          match.ref.status }}</span>
                        <router-link :to="'/authorization-code/' + match.ref.id">
                          <button type="button" class="btn btn-success btn-sm">
                            <i class="fe fe-eye mr-1"></i> View
                          </button>
                        </router-link>
                      </div>
                    </div>
                  </div>

                  <!-- No match -->
                  <div v-else-if="faceScan.capturedImage && !faceScan.scanning" class="text-center py-3">
                    <div class="alert alert-warning d-flex align-items-center mb-0">
                      <i class="fe fe-alert-circle mr-2" style="font-size: 1.2rem;"></i>
                      <span>No matching referral found for this face in the current list.</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ── VERIFY BY ID ────────────────────────────────────── -->
          <div class="card shadow-sm mb-4">
            <div class="card-header border-0 pt-4 pb-2 px-4" style="background: #f8f9fa;">
              <div class="d-flex align-items-center">
                <i class="fe fe-search text-info mr-2"></i>
                <h6 class="mb-0 font-weight-bold text-dark">Verify Referral by ID</h6>
              </div>
            </div>
            <div class="card-body px-4 pt-3 pb-3">
              <div class="input-group">
                <div class="input-group-prepend">
                  <span class="input-group-text bg-white"><i class="fe fe-hash text-muted"></i></span>
                </div>
                <input type="text" class="form-control border-left-0" v-model="searchkey"
                  placeholder="Enter Enrollee ID…" @keyup.enter="verifyRef" />
                <div class="input-group-append">
                  <button type="button" class="btn btn-info px-4" @click="verifyRef">
                    <i class="fe fe-search mr-1"></i> Search
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- ── REFERRALS TABLE ─────────────────────────────────── -->
          <div class="card shadow-sm">
            <div class="card-header border-0 pt-3 pb-3 px-4" style="background: #f8f9fa;">
              <div class="d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <i class="fe fe-list text-info mr-2"></i>
                  <h6 class="mb-0 font-weight-bold text-dark">Referral List</h6>
                  <span v-if="referrals.data" class="badge badge-soft-info ml-2">{{ referrals.data.length }}</span>
                </div>
                <div class="d-flex align-items-center" style="gap:8px;">
                  <div class="view-toggle">
                    <button :class="['view-toggle-btn', viewMode === 'list' ? 'active' : '']" @click="viewMode = 'list'" title="List view"><i class="fe fe-list"></i></button>
                    <button :class="['view-toggle-btn', viewMode === 'grid' ? 'active' : '']" @click="viewMode = 'grid'" title="Grid view"><i class="fe fe-grid"></i></button>
                  </div>
                <button class="btn btn-outline-info btn-sm">
                  <download-excel :data="referrals.data" :fields="json_fields" type="csv" :escapeCsv="false"
                    :name="'referrals_data.csv'">
                    <i class="fe fe-download mr-1"></i> Export
                  </download-excel>
                </button>
                </div>
              </div>
            </div>
            <div class="card-body p-0" v-if="viewMode === 'list'">
              <div class="table-responsive">
                <table class="table table-hover mb-0" style="min-width: 700px;">
                  <thead style="background: #f8f9fa;">
                    <tr>
                      <th class="px-4 py-3 border-0"
                        style="font-size: 0.75rem; text-transform: uppercase; letter-spacing: .04em; color: #6c757d;">
                        Enrollee</th>
                      <th class="py-3 border-0"
                        style="font-size: 0.75rem; text-transform: uppercase; letter-spacing: .04em; color: #6c757d;">
                        Date</th>
                      <th class="py-3 border-0"
                        style="font-size: 0.75rem; text-transform: uppercase; letter-spacing: .04em; color: #6c757d;">
                        Referring HCP</th>
                      <th class="py-3 border-0"
                        style="font-size: 0.75rem; text-transform: uppercase; letter-spacing: .04em; color: #6c757d;">
                        Recipient HCP</th>
                      <th class="py-3 border-0"
                        style="font-size: 0.75rem; text-transform: uppercase; letter-spacing: .04em; color: #6c757d;">
                        Status</th>
                      <th class="py-3 border-0"
                        style="font-size: 0.75rem; text-transform: uppercase; letter-spacing: .04em; color: #6c757d;">
                        Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="ref in referrals.data" :key="ref.id" style="vertical-align: middle;">
                      <td class="px-4 py-3">
                        <div class="d-flex align-items-center">
                          <img v-if="ref.enrollee && ref.enrollee.picture_available === 'image_available'"
                            :src="ref.enrollee.image_url" class="rounded-circle mr-2"
                            style="width: 38px; height: 38px; object-fit: cover; flex-shrink: 0; border: 2px solid #e3eaf0;"
                            onerror="this.onerror=null; this.src='/assets/img/claimify_logo.png'" />
                          <div v-else
                            class="rounded-circle mr-2 d-flex align-items-center justify-content-center bg-light"
                            style="width: 38px; height: 38px; min-width: 38px;">
                            <i class="fe fe-user text-muted" style="font-size: 0.85rem;"></i>
                          </div>
                          <div v-if="ref.enrollee">
                            <p class="mb-0 font-weight-bold" style="font-size: 0.85rem; line-height: 1.2;">{{
                              ref.enrollee.full_name }}</p>
                            <small class="text-muted">{{ ref.enrollee.id_card_number }}</small>
                          </div>
                        </div>
                      </td>
                      <td class="py-3">
                        <router-link :to="'/authorization-code/' + ref.id" class="text-info font-weight-bold"
                          style="font-size: 0.85rem;">
                          {{ ref.created_at | moment("MMM Do YYYY") }}
                        </router-link>
                        <br><small class="text-muted">{{ ref.created_at | moment("from", "now") }}</small>
                      </td>
                      <td class="py-3" style="font-size: 0.85rem;">{{ ref.provider && ref.provider.agency_name }}</td>
                      <td class="py-3" style="font-size: 0.85rem;">{{ ref.recipientfacility &&
                        ref.recipientfacility.agency_name }}</td>
                      <td class="py-3">
                        <span :class="`badge badge-soft-${ref.color || 'secondary'} mr-1`"
                          style="font-size: 0.75rem;">{{ ref.status }}</span>
                        <span class="badge badge-soft-dark mr-1" style="font-size: 0.75rem;">{{ ref.code_usage }}</span>
                        <br>
                        <button class="btn btn-outline-info btn-sm mt-1" @click="copyCode(ref)"
                          style="font-size: 0.75rem; padding: 2px 8px;">
                          {{ ref.code_created }} <i class="fe fe-copy ml-1"></i>
                        </button>
                      </td>
                      <td class="py-3">
                        <div class="btn-group btn-group-sm">
                          <router-link :to="'/authorization-code/' + ref.id">
                            <button type="button" class="btn btn-outline-info btn-sm">
                              <i class="fe fe-eye"></i>
                            </button>
                          </router-link>
                          <button type="button"
                            class="btn btn-outline-info btn-sm dropdown-toggle dropdown-toggle-split"
                            data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                          </button>
                          <div class="dropdown-menu dropdown-menu-right">
                            <a class="dropdown-item" role="button" @click="$router.push({ path: '/add-record', query: { enrollee: ref.enrollee.id_card_number, type: 'secondary', reason: 'Referred In' } })">
                              <i class="fe fe-file-plus mr-2 text-primary"></i> Claim
                            </a>
                            <a class="dropdown-item" role="button" @click="openReferAgain(ref)">
                              <i class="fe fe-repeat mr-2 text-info"></i> Transfer
                            </a>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="!referrals.data || !referrals.data.length">
                      <td colspan="6" class="text-center py-5 text-muted">
                        <i class="fe fe-inbox" style="font-size: 2.5rem; opacity: 0.3;"></i>
                        <p class="mt-2 mb-0">No referrals found</p>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="refs-grid" v-if="viewMode === 'grid'">
              <div v-if="!referrals.data || !referrals.data.length" style="grid-column:1/-1" class="text-center py-5 text-muted">
                <i class="fe fe-inbox" style="font-size:2.5rem;opacity:0.3"></i>
                <p class="mt-2 mb-0">No referrals found</p>
              </div>
              <div v-for="ref in referrals.data" :key="ref.id" class="ref-grid-card">
                <div class="d-flex align-items-center mb-2" style="gap:10px;">
                  <img v-if="ref.enrollee && ref.enrollee.picture_available === 'image_available'"
                    :src="ref.enrollee.image_url" class="rounded-circle"
                    style="width:40px;height:40px;object-fit:cover;border:2px solid #e3eaf0;flex-shrink:0;"
                    onerror="this.onerror=null;this.src='/assets/img/claimify_logo.png'" />
                  <div v-else class="rounded-circle d-flex align-items-center justify-content-center bg-light" style="width:40px;height:40px;min-width:40px;">
                    <i class="fe fe-user text-muted" style="font-size:.85rem;"></i>
                  </div>
                  <div v-if="ref.enrollee">
                    <div class="font-weight-bold" style="font-size:.88rem;line-height:1.2;">{{ ref.enrollee.full_name }}</div>
                    <small class="text-muted">{{ ref.enrollee.id_card_number }}</small>
                  </div>
                </div>
                <div class="mb-2" style="font-size:.82rem;color:#64748b;">
                  <div><i class="fe fe-arrow-up-right mr-1 text-muted"></i>{{ ref.provider && ref.provider.agency_name }}</div>
                  <div><i class="fe fe-arrow-down-left mr-1 text-muted"></i>{{ ref.recipientfacility && ref.recipientfacility.agency_name }}</div>
                </div>
                <div class="d-flex align-items-center mb-2" style="gap:6px;">
                  <span :class="`badge badge-soft-${ref.color || 'secondary'}`" style="font-size:.75rem;">{{ ref.status }}</span>
                  <button class="btn btn-outline-info btn-sm" @click="copyCode(ref)" style="font-size:.72rem;padding:2px 8px;">
                    {{ ref.code_created }} <i class="fe fe-copy ml-1"></i>
                  </button>
                </div>
                <div class="d-flex justify-content-between align-items-center grid-card-footer">
                  <small class="text-muted">{{ ref.created_at | moment("MMM Do YYYY") }}</small>
                  <router-link :to="'/authorization-code/' + ref.id">
                    <button type="button" class="btn btn-outline-info btn-sm"><i class="fe fe-eye mr-1"></i>View</button>
                  </router-link>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      <!-- ── Refer Again Modal ─────────────────────────────────── -->
      <div class="modal fade" id="referAgainModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog" role="document">
          <div class="modal-content">
            <div class="modal-header" style="background: linear-gradient(135deg, #1e3a5f, #2563eb);">
              <div class="d-flex align-items-center">
                <div class="header-icon-wrap mr-3" style="width:36px;height:36px;">
                  <i class="fe fe-repeat" style="font-size:16px;color:#fff;"></i>
                </div>
                <h5 class="modal-title mb-0 text-white font-weight-bold">Update Referred Facility</h5>
              </div>
              <button type="button" class="close text-white ml-auto" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div class="modal-body">
              <!-- Enrollee card -->
              <div v-if="referAgainModal.ref" class="d-flex align-items-center p-3 rounded mb-4"
                style="background: #f0f4ff; border: 1px solid #d0d9ee;">
                <img
                  v-if="referAgainModal.ref.enrollee && referAgainModal.ref.enrollee.picture_available === 'image_available'"
                  :src="referAgainModal.ref.enrollee.image_url" class="rounded-circle mr-3"
                  style="width: 48px; height: 48px; object-fit: cover; border: 2px solid #2563eb; flex-shrink: 0;"
                  onerror="this.onerror=null; this.src='/assets/img/claimify_logo.png'" />
                <div v-else class="rounded-circle mr-3 d-flex align-items-center justify-content-center bg-white"
                  style="width: 48px; height: 48px; min-width: 48px; border: 2px solid #2563eb;">
                  <i class="fe fe-user text-info"></i>
                </div>
                <div>
                  <p class="mb-0 font-weight-bold" style="font-size: 0.9rem;">
                    {{ referAgainModal.ref.enrollee && referAgainModal.ref.enrollee.full_name }}
                  </p>
                  <small class="text-muted">
                    {{ referAgainModal.ref.enrollee && referAgainModal.ref.enrollee.id_card_number }}
                  </small>
                </div>
              </div>

              <div class="form-group mb-0">
                <label class="font-weight-bold" style="font-size: 0.85rem;">
                  Referred To Facility <span class="text-danger">*</span>
                </label>
                <v-select v-model="referAgainModal.recipientFacility" label="agency_name" :options="providers"
                  placeholder="Search provider…" />
              </div>
            </div>

            <div class="modal-footer">
              <button type="button" class="btn btn-outline-secondary" data-dismiss="modal">Cancel</button>
              <button type="button" class="btn btn-submit" @click="submitReferAgain"
                :disabled="referAgainModal.submitting || !referAgainModal.recipientFacility">
                <span v-if="referAgainModal.submitting">
                  <span class="spinner-border spinner-border-sm mr-1"></span> Updating…
                </span>
                <span v-else><i class="fe fe-check mr-1"></i> Update Facility</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
      </div>
    </main>
  </section>
</template>

<script>
  import * as faceapi from "face-api.js";
  import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
  import Navbar from "@/views/Navbar.vue";
  import Loading from "vue-loading-overlay";
  import "vue-loading-overlay/dist/vue-loading.css";

  let agency = JSON.parse(localStorage.getItem("agency"));

  export default {
    components: { Navbar, Loading },

    data() {
      return {
        user: null,
        searchkey: "",
        referrals: { data: [], meta: { total: 0 } },
        provider_referral_category: "secondary",
        isLoading: false,
        fullPage: true,
        buttoncolor: {
          informal: "btn btn-primary",
          formal: "btn btn-outline-secondary",
        },
        faceScan: {
          active: false,
          loading: false,
          scanning: false,
          capturedImage: null,
          matches: [],
          progress: 0,
          total: 0,
        },
        json_fields: {
          "Patient Name": "client.full_name",
          "Patient Phone Number": "client.phone_number",
          "Referring Facility": "referring.agency_name",
          "Receiving Facility": "referred.agency_name",
          Diagnosis: "diagnosis.name",
          "Date Created": "created_at",
        },
        json_meta: [[{ key: "charset", value: "utf-8" }]],
        providers: [],
        referAgainModal: {
          ref: null,
          recipientFacility: null,
          submitting: false,
        },
        viewMode: 'list',
      };
    },

    beforeMount() {
      this.user = JSON.parse(localStorage.getItem("user"));
    },

    mounted() {
      this.viewMode = window.innerWidth < 768 ? 'grid' : 'list';
    },

    created() {
      this.getReferrals();
      this.getProviders();
    },

    methods: {
      showPrimary() {
        this.provider_referral_category = "primary";
        this.buttoncolor.formal = "btn btn-primary";
        this.buttoncolor.informal = "btn btn-outline-secondary";
        this.getReferrals();
      },

      showSecondary() {
        this.provider_referral_category = "secondary";
        this.buttoncolor.informal = "btn btn-primary";
        this.buttoncolor.formal = "btn btn-outline-secondary";
        this.getReferrals();
      },

      getReferrals() {
        this.user = JSON.parse(localStorage.getItem("user"));
        this.isLoading = true;
        let url;
        if (this.user.type === "shis" || this.user.type === "employee") {
          url = `/api/v1/auth/referrals-agency/${agency.id}`;
        } else if (this.user.type === "provider") {
          url = `/api/v1/auth/referrals-${this.provider_referral_category}/${this.user.id}`;
        } else if (this.user.type === "provider_employee") {
          url = `/api/v1/auth/referrals-${this.provider_referral_category}/${this.user.institutional_id}`;
        }
        if (!url) return;
        this.axios.get(url)
          .then((response) => { this.referrals = response.data; })
          .catch((error) => { console.error(error); })
          .finally(() => { this.isLoading = false; });
      },

      verifyRef() {
        this.isLoading = true;
        this.axios
          .post(`/api/v1/auth/verifyReferal`, { searchkey: this.searchkey })
          .then((response) => {
            this.referrals = response.data;
            this.$toasted.info("Search complete", { position: "top-center", duration: 2000 });
          })
          .catch((error) => { console.error(error); })
          .finally(() => { this.isLoading = false; });
      },

      copyCode(ref) {
        navigator.clipboard.writeText(ref.code_created);
        this.$toasted.info("Copied to clipboard", { position: "top-center", duration: 2000 });
      },

      // ── Face Scan ────────────────────────────────────────────────

      async startFaceScan() {
        this.faceScan.active = true;
        this.faceScan.loading = true;

        if (!faceapi.nets.tinyFaceDetector.isLoaded) {
          try {
            const MODEL_URL = "/models";
            await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
            await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
            await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
          } catch (e) {
            console.error("Model load failed", e);
            this.$toasted.error("Failed to load face models.", { position: "top-center", duration: 4000 });
            this.faceScan.loading = false;
            return;
          }
        }

        this.faceScan.loading = false;
        await this.captureForScan();
      },

      async captureForScan() {
        try {
          const photo = await Camera.getPhoto({
            quality: 90,
            resultType: CameraResultType.Base64,
            source: CameraSource.Camera,
            format: "jpeg",
          });
          this.faceScan.capturedImage = `data:image/jpeg;base64,${photo.base64String}`;
          this.faceScan.matches = [];
          this.scanAllReferrals();
        } catch (e) {
          console.error("Camera error", e);
        }
      },

      async scanAllReferrals() {
        this.faceScan.scanning = true;
        this.faceScan.matches = [];

        try {
          const opts = new faceapi.TinyFaceDetectorOptions({ inputSize: 608, scoreThreshold: 0.5 });

          // Get descriptor from the captured live photo
          const capturedEl = document.createElement("img");
          capturedEl.src = this.faceScan.capturedImage;
          await new Promise((res) => { capturedEl.onload = res; });
          const capturedDetection = await faceapi
            .detectSingleFace(capturedEl, opts)
            .withFaceLandmarks()
            .withFaceDescriptor();

          if (!capturedDetection) {
            this.$toasted.error("No face detected. Try again in better lighting.", { position: "top-center", duration: 3000 });
            this.faceScan.scanning = false;
            return;
          }

          // Build unique enrollee → referrals map (skip no-image enrollees)
          const referrals = this.referrals.data || [];
          const enrolleeMap = new Map();
          for (const ref of referrals) {
            if (ref.enrollee && ref.enrollee.picture_available === "image_available" && ref.enrollee.user_image) {
              const id = ref.enrollee.id;
              if (!enrolleeMap.has(id)) enrolleeMap.set(id, { ref, refs: [] });
              enrolleeMap.get(id).refs.push(ref);
            }
          }

          const uniqueEntries = [...enrolleeMap.values()];
          this.faceScan.total = uniqueEntries.length;
          this.faceScan.progress = 0;

          for (const { ref, refs } of uniqueEntries) {
            this.faceScan.progress++;
            try {
              const imageUrl = `https://claimify-api.hayokmedicare.ng/api/v1/auth/image/${ref.enrollee.user_image}`;
              const storedImg = await faceapi.fetchImage(imageUrl);
              const storedDetection = await faceapi
                .detectSingleFace(storedImg, opts)
                .withFaceLandmarks()
                .withFaceDescriptor();

              if (storedDetection) {
                const distance = faceapi.euclideanDistance(capturedDetection.descriptor, storedDetection.descriptor);
                if (distance < 0.5) {
                  const confidence = Math.round(Math.max(0, (1 - distance) * 100));
                  for (const matchRef of refs) {
                    this.faceScan.matches.push({ ref: matchRef, confidence, distance });
                  }
                }
              }
            } catch (e) {
              console.warn(`Skipped ${ref.enrollee.full_name}:`, e.message);
            }
          }

          this.faceScan.matches.sort((a, b) => b.confidence - a.confidence);

        } catch (e) {
          console.error("Scan failed", e);
          this.$toasted.error("Scan failed. Try again.", { position: "top-center", duration: 3000 });
        } finally {
          this.faceScan.scanning = false;
        }
      },

      getProviders() {
        this.axios
          .get(`/api/v1/auth/providerAgency/${agency.id}`)
          .then((response) => { this.providers = response.data.data; })
          .catch((error) => { console.error(error); });
      },

      openReferAgain(ref) {
        this.referAgainModal.ref = ref;
        this.referAgainModal.recipientFacility = null;
        this.referAgainModal.submitting = false;
        // eslint-disable-next-line no-undef
        $('#referAgainModal').modal('show');
      },

      submitReferAgain() {
        const { ref, recipientFacility } = this.referAgainModal;
        if (!recipientFacility) return;

        this.referAgainModal.submitting = true;
        this.axios
          .post("/api/v1/auth/updateExpiry", {
            id: ref.id,
            expiry_date: ref.expiry_date,
            referred_to_facility: recipientFacility.id,
          })
          .then(() => {
            this.referAgainModal.submitting = false;
            // eslint-disable-next-line no-undef
            $('#referAgainModal').modal('hide');
            this.$toasted.success("Facility updated successfully!", { position: "top-center", duration: 3000 });
            this.getReferrals();
          })
          .catch((error) => {
            console.error(error);
            this.referAgainModal.submitting = false;
            this.$toasted.error("Failed to update facility.", { position: "top-center", duration: 3000 });
          });
      },

      resetFaceScan() {
        this.faceScan.active = false;
        this.faceScan.capturedImage = null;
        this.faceScan.matches = [];
        this.faceScan.scanning = false;
        this.faceScan.progress = 0;
        this.faceScan.total = 0;
      },
    },
  };
</script>

<style scoped>
  .card {
    border-radius: 12px;
    border: 1px solid #e9ecef;
  }

  .card-header {
    border-radius: 12px 12px 0 0 !important;
  }

  .table-hover tbody tr:hover {
    background: #f0f4ff;
  }

  .form-control:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 0.15rem rgba(37, 99, 235, 0.15);
  }

  /* ── View toggle ── */
  .view-toggle { display: flex; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
  .view-toggle-btn { background: none; border: none; padding: 6px 10px; color: #94a3b8; cursor: pointer; font-size: .85rem; line-height: 1; }
  .view-toggle-btn.active { background: #1e3a5f; color: #fff; }
  .view-toggle-btn:hover:not(.active) { background: #f1f5f9; color: #475569; }

  /* ── Grid view ── */
  .refs-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 16px;
    padding: 20px 24px;
  }
  .ref-grid-card {
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 16px;
    background: #fff;
    display: flex;
    flex-direction: column;
    transition: border-color .15s, box-shadow .15s;
  }
  .ref-grid-card:hover { border-color: #93c5fd; box-shadow: 0 2px 10px rgba(37,99,235,.1); }
  .grid-card-footer { border-top: 1px solid #f1f5f9; padding-top: 10px; margin-top: auto; }
</style>